import {
	jsonCircularSafeStringify,
	obfuscateCredentials,
} from "../utils/string.utils";
import { LOG_LEVEL, LogEntry, LogMeta } from "./TXLogger.interfaces";

export class TXLogger {
	private static instance?: TXLogger;

	static getInstance = (serviceName?: string) => {
		if (!TXLogger.instance) {
			if (!serviceName) {
				throw `No "serviceName" supplied. Make sure to initialize TXLogger with a service name.`;
			}

			TXLogger.instance = new TXLogger(serviceName);
		} else if (TXLogger.instance.serviceName && serviceName) {
			throw `Do not instantiate TXLogger with a serviceName more than once. This should be declared in the main entrypoint file. Current service name ${TXLogger.instance.serviceName}; new service name ${serviceName}.`;
		}
		return TXLogger.instance;
	};

	constructor(serviceName?: string) {
		this.serviceName = serviceName;
		TXLogger.instance = this;
	}

	// only set once; eg: TXLogger.getInstance("ipad-app-client"); at top-level file for the current app
	private serviceName?: string;
	private minLogLevelServer = 10;

	private log = (
		component: string,
		message: string,
		level: number,
		metadata: LogMeta
	) => {
		if (level < this.minLogLevelServer) {
			return;
		}

		const _level =
			(Object.entries(LOG_LEVEL).find(
				([key, value]) => value == level
			)?.[0] as keyof typeof LOG_LEVEL) || "trace";

		const logEntry: LogEntry = {
			message: component + " :: " + message,
			level: _level,
			level_id: level,
			timestamp: new Date().toISOString(),

			tx: {
				service: this.serviceName!,
				env:
					process.env.REACT_APP_ENVIRONMENT ||
					process.env.ENVIRONMENT ||
					"localdev",

				...this.processMeta(metadata),

				platform:
					metadata.platform || process.env.IS_SERVER ? "server" : "webapp",
			},
		};

		if (process.env.IS_SERVER) {
			process.stdout.write(jsonCircularSafeStringify(logEntry) + "\n");
		} else {
			const log = logEntry;

			switch (_level) {
				case "error":
					console.error(log);
					break;
				case "warn":
					console.warn(log);
					break;
				default:
					console.log(log);
			}
		}
	};

	private processMeta(metadata: LogMeta) {
		if (metadata.error) {
			metadata.error = obfuscateCredentials(metadata.error);
		}
		if (metadata.requestConfig) {
			const requestConfig = obfuscateCredentials(metadata.requestConfig);

			if (
				metadata.requestConfig.params &&
				metadata.requestConfig.paramsSerializer &&
				"serialize" in metadata.requestConfig.paramsSerializer
			) {
				requestConfig.params =
					metadata.requestConfig.paramsSerializer.serialize(
						requestConfig.params
					);

				delete requestConfig.paramsSerializer;
			}

			metadata.requestConfig = requestConfig;
		}

		return metadata;
	}

	static trace(component: string, message: string, metadata: LogMeta) {
		TXLogger.getInstance().log(component, message, LOG_LEVEL.trace, metadata);
	}
	static debug(component: string, message: string, metadata: LogMeta) {
		TXLogger.getInstance().log(component, message, LOG_LEVEL.debug, metadata);
	}
	static info(component: string, message: string, metadata: LogMeta) {
		TXLogger.getInstance().log(component, message, LOG_LEVEL.info, metadata);
	}
	static warn(component: string, message: string, metadata: LogMeta) {
		TXLogger.getInstance().log(component, message, LOG_LEVEL.warn, metadata);
	}
	static error(component: string, message: string, metadata: LogMeta) {
		TXLogger.getInstance().log(component, message, LOG_LEVEL.error, metadata);
	}
	static fatal(component: string, message: string, metadata: LogMeta) {
		TXLogger.getInstance().log(component, message, LOG_LEVEL.fatal, metadata);
	}
}
